import {
	TableRow,
	TableColumn,
	SelectField,
	Button,
	DataTable,
	TableHeader,
	TableBody,
} from 'react-md'
import {
	billingBasisMessage,
	meterBillingTypeMessage,
	meterTypeMessage,
} from 'ui/ManagerFinance/utils/messages'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import type { ListValue } from 'react-md/lib/SelectFields/SelectField'
import type { PaymentProduct } from 'ui/ManagerFinance/PaymentProducts/types'
import { meterInvoicePaymentProductHeaders } from './constants'
import { createPaymentProductOptions } from 'ui/ManagerFinance/utils/paymentProductOptions'
import { useFormContext, useWatch } from 'react-hook-form'
import type { MeterInvoicingForm } from './types'

const Content = styled.div`
	overflow: auto;
`

type Props = {
	paymentProducts: PaymentProduct[]
}
const PaymentProductsForm = ({ paymentProducts }: Props) => {
	const intl = useIntl()

	const { setValue } = useFormContext<MeterInvoicingForm>()

	const [paymentProductUUIDs, invoiceDate] = useWatch({
		name: ['paymentProductUUIDs', 'invoiceDate'],
	})

	const paymentProductOptions = createPaymentProductOptions(paymentProducts)

	const deleteRow = (uuid: string) => {
		const values = paymentProductUUIDs.filter((v) => v !== uuid)
		setValue('paymentProductUUIDs', values)
	}

	const handleSelectChange = async (value: ListValue, field: string) => {
		if (field === 'paymentProductUUIDs') {
			const values = [...(paymentProductUUIDs || []), value as string]
			setValue(field, values)
		}
	}

	const renderTableHeaders = () => {
		return (
			<TableRow>
				{meterInvoicePaymentProductHeaders.map((header) => (
					<TableColumn key={header.key}>
						<p className="text-strong text-subtle">{header.title(intl)}</p>
					</TableColumn>
				))}
			</TableRow>
		)
	}

	const getValue = (paymentProduct: PaymentProduct, key: string) => {
		let value = paymentProduct[key]

		if (key === 'billingBasis') {
			return billingBasisMessage(intl, value)
		} else if (key === 'meterType') {
			return meterTypeMessage(intl, value)
		} else if (key === 'meterBillingType') {
			return meterBillingTypeMessage(intl, value)
		} else if (key === 'priceWithVat') {
			//TODO: MAYBE AN ERROR HERE IF NO PRICES? OR DONE IN BE
			const currentDate = new Date(invoiceDate)

			const price = paymentProduct.prices.find((p) => {
				let startDate = new Date(p.startDate)
				let endDate = p.endDate ? new Date(p.endDate) : null

				return currentDate >= startDate && (!endDate || currentDate <= endDate)
			})
			return price?.priceWithVat
		} else {
			return value || ''
		}
	}

	const renderColumns = (paymentProduct: PaymentProduct) => {
		return meterInvoicePaymentProductHeaders.map((header) => {
			const { key } = header

			const value = getValue(paymentProduct, key)

			return <TableColumn key={key}>{value}</TableColumn>
		})
	}

	const renderSelectedPaymentProducts = () => {
		if (!paymentProductUUIDs || paymentProductUUIDs.length === 0) return []
		return paymentProductUUIDs
			.flatMap((uuid) => {
				const paymentProduct = paymentProducts.find((p) => p.uuid === uuid)
				if (!paymentProduct) return []
				return (
					<TableRow key={uuid}>
						{renderColumns(paymentProduct)}
						<TableColumn key={'delete'}>
							<Button icon onClick={() => deleteRow(uuid)}>
								delete
							</Button>
						</TableColumn>
					</TableRow>
				)
			})
			.filter(Boolean)
	}

	return (
		<Content>
			<p style={{ padding: '0rem 1.5rem' }}>
				<FormattedMessage
					defaultMessage="Kaikki valitut maksulajit tulee liittää sopimukseen, jotta sopimusvalinnassa voidaan tarjota parhaat mahdolliset vaihtoehdot. Maksulajit voi lisätä sopimukselle Sopimukset-osiosta."
					description="All selected payment products must be linked to the contract to provide the best possible options in contract selection. You can add payment products to a contract in the Contracts section."
				/>
			</p>
			<SelectField
				id="payment-product-select"
				placeholder={intl.formatMessage({
					defaultMessage: 'Maksulajit',
					description: 'Placeholder of payment products',
				})}
				value={''}
				onChange={(value) => handleSelectChange(value, 'paymentProductUUIDs')}
				menuItems={paymentProductOptions.filter((item) => {
					return !paymentProductUUIDs?.includes(item.value)
				})}
				position={SelectField.Positions.BELOW}
				simplifiedMenu={false}
				listHeightRestricted
				style={{
					marginTop: '20px',
					width: '100%',
					background: '#fafafa',
					marginBottom: '10px',
				}}
			/>
			<DataTable plain>
				<TableHeader>{renderTableHeaders()}</TableHeader>
				<TableBody>{renderSelectedPaymentProducts()}</TableBody>
			</DataTable>
		</Content>
	)
}

export default PaymentProductsForm
